import { useCallback, useEffect, useRef, useState } from 'react';

import { AcquisitionProductId } from './acquisitionProducts';

/** What one product's panel is doing that the workspace around it must know. */
export interface AcquisitionActivity {
  /** A create, status or retry request is in flight for this product. */
  busy: boolean;
  /** Work that leaving the page would lose: a draft, an unresolved operation. */
  unsaved: boolean;
}

const IDLE: AcquisitionActivity = { busy: false, unsaved: false };

export interface AcquisitionActivityStore {
  /** Whether any product has a request in flight. */
  isBusy: boolean;
  /** Whether leaving the workspace would lose work of any product. */
  hasUnsavedWork: boolean;
  report: (product: AcquisitionProductId, activity: AcquisitionActivity) => void;
}

/**
 * What the workspace knows about the compilations inside it.
 *
 * Only the selected product's panel is mounted, so the page cannot read a
 * panel's state and an unmounted product would otherwise look idle and clean —
 * although its draft is still held above the panels. Each panel reports what it
 * is doing instead, and the workspace keeps the last word of every product:
 * that is what lets the tabs lock during a request and what lets a navigation
 * away be questioned for work the operator cannot currently see.
 *
 * This is state rather than a ref precisely because it is rendered: a tab's
 * disabled state and the navigation guard both change with it.
 */
export const useAcquisitionActivity = (): AcquisitionActivityStore => {
  const [activity, setActivity] = useState<
    Partial<Record<AcquisitionProductId, AcquisitionActivity>>
  >({});

  const report = useCallback(
    (product: AcquisitionProductId, next: AcquisitionActivity) => {
      setActivity((current) => {
        const previous = current[product];
        // A panel reports on every render of its own; an unchanged report must
        // not re-render the workspace that owns that panel.
        if (
          previous &&
          previous.busy === next.busy &&
          previous.unsaved === next.unsaved
        ) {
          return current;
        }
        return { ...current, [product]: next };
      });
    },
    []
  );

  const reported = Object.values(activity);

  return {
    isBusy: reported.some((entry) => entry.busy),
    hasUnsavedWork: reported.some((entry) => entry.unsaved),
    report,
  };
};

/**
 * Keeps the workspace's picture of one product current while its panel lives.
 *
 * On unmount the panel's requests stop concerning anyone — nothing consumes
 * their answer any more — but the draft it just handed back to the session
 * store does not go away with it, so the last word is "idle, and still
 * unsaved".
 */
export const useAcquisitionActivityReport = (
  activity: AcquisitionActivityStore,
  product: AcquisitionProductId,
  current: AcquisitionActivity
) => {
  const { report } = activity;
  const { busy, unsaved } = current;

  useEffect(() => {
    report(product, { busy, unsaved });
  }, [report, product, busy, unsaved]);

  const unsavedRef = useRef(unsaved);
  unsavedRef.current = unsaved;

  useEffect(
    () => () => report(product, { ...IDLE, unsaved: unsavedRef.current }),
    [report, product]
  );
};
